import * as z from 'zod';

import { CollaboratorSchema } from './collaborators';
import { DateSchema, NumberSchema } from './common/processed';
import { EventTemplateFieldsSchema } from './eventField';
import { EscalationDetailsSchema } from './escalationDetails';
import { OptionalStringArraySchema, OptionalStringSchema } from './commonZodSchemas/strings';
import { OptionalNumberArraySchema } from './commonZodSchemas/number';
import { EventStatusSchema } from './eventStatus';

export const QualityEventStatus = z.enum(['open', 'closed', 'overdue']);
export type QualityEventStatus = z.infer<typeof QualityEventStatus>;

export const IntegrationFields = z.record(z.string(), z.any());
export type IntegrationFields = z.infer<typeof IntegrationFields>;

export const QeSource = z.object({
  company_id: z.number(),
  created_at: DateSchema,
  id: NumberSchema,
  name: z.string(),
  type: z.string(),
  url: z.string(),
});

export type QeSource = z.infer<typeof QeSource>;

export const ZProductRootCause = z.object({
  matrix_id: z.string(),
  name: z.string(),
  qri: z.string(),
});
export type ProductRootCause = z.infer<typeof ZProductRootCause>;

export const ListQualityEvent = z.object({
  id: NumberSchema,
  uuid: z.string(),
  code: z.string(),
  title: z.string(),
  created_at: DateSchema,
  updated_at: DateSchema,
  resolved_at: DateSchema.nullable(),
  description: z.string(),
  workflow_id: z.number(),
  workflow: z.string(),
  due_at: DateSchema,
  due_date: z.string().optional(),
  status: EventStatusSchema,
  owner: z.string().nullable(),
  owner_id: z.number(),
  email: z.string().optional().nullable(),
  creator: z.string().nullable(),
  ttr: z.number().optional().nullable(),
  risk: z.string().optional().nullable(),
  risk_id: z.number().optional().nullable(),
  severity_id: z.number().optional().nullable(),
  severity: z.string().optional().nullable(),
  probability_id: z.number().optional().nullable(),
  probability: z.string().optional().nullable(),
  time_limit: z.number(),
  tags: z.string().default(''), // TODO: this should be removed and tags_ids should be used
  tags_ids: z.array(z.number()),
  steps_closed: z.number(),
  steps_total: z.number(),
  template_fields: EventTemplateFieldsSchema,
  products: z.array(ZProductRootCause).optional(),
  rootcauses: z.array(ZProductRootCause).optional(),
  collaborators: z.array(CollaboratorSchema).optional().default([]),
});

export const QualityEvent = ListQualityEvent.extend({
  email: z.string().optional().nullable(),
  company_id: z.number().optional(),
  severity: z.string().optional().nullable(),
  probability: z.string().optional().nullable(),
  related_entity_ids: z.array(z.string()),
  suppliers: z.array(z.string()).default([]),
  related_documents: z
    .array(z.string())
    .optional()
    .transform((val) => val ?? []),
  related_events: z
    .array(z.string())
    .optional()
    .transform((val) => val ?? []),
  current_step: z.string(),
  acknowledged_at: DateSchema.nullable(),
  qe_source: QeSource.nullable(),
  fields: IntegrationFields.nullable(),
  escalated_to: z.array(EscalationDetailsSchema).optional().nullable(),
  escalated_from: z.array(EscalationDetailsSchema).optional().nullable(),
  due_date: z.string().optional(),
  workflow_major_version: z.number().optional().nullable(),
  workflow_minor_version: z.number().optional().nullable(),
});
export type ListQualityEvent = z.infer<typeof ListQualityEvent>;
export type QualityEvent = z.infer<typeof QualityEvent>;

export const QualityEventSyncSchema = QualityEvent.pick({
  id: true,
}).merge(
  z.object({
    template_fields: EventTemplateFieldsSchema,
  }),
);

export type QualityEventSync = z.infer<typeof QualityEventSyncSchema>;

export const QualityEventListResponse = z.object({
  data: z.array(ListQualityEvent),
  meta: z.object({
    page: z.object({
      page_size: z.number(),
      total_items: z.number(),
      total_pages: z.number(),
    }),
  }),
});

export type QualityEventListResponse = z.infer<typeof QualityEventListResponse>;

export const RelatedRecordRef = z.object({
  domain: z.string(),
  type: z.string(),
  id: z.string(),
  version_id: z.string().optional(),
});

export type RelatedRecordRef = z.infer<typeof RelatedRecordRef>;

/**
 * Reserved sentinel that rides inside the events-search id-array filters
 * (product_ids / rootcause_ids / filter_tags / severity_ids / probability_ids)
 * to signal "match events that have no value for this field". Mirrors the
 * backend IS_EMPTY_SENTINEL and must never collide with a real id.
 */
export const EMPTY_FILTER_VALUE = '__is_empty' as const;

export const QualityEventsGet = z.object({
  event_type: z.string().optional(),
  event_types: z.array(z.string()).optional(),
  created_at_start_date: z.string().optional(),
  created_at_end_date: z.string().optional(),
  due_at_start_date: z.string().optional(),
  due_at_end_date: z.string().optional(),
  resolved_at_start_date: z.string().optional(),
  resolved_at_end_date: z.string().optional(),
  order_by: z.string().nullable().optional(),
  status: z.array(z.string()).optional(),
  owner_id: z.string().optional(),
  owner_ids: z.array(z.string()).optional(),
  collaborator_ids: z.array(z.string()).optional(),
  filter_tags: z.array(z.string()).optional(),
  product_id: z.string().optional(),
  product_ids: z.array(z.string()).optional(),
  rootcause_id: z.string().optional(),
  rootcause_ids: z.array(z.string()).optional(),
  severity_ids: z.array(z.union([z.number(), z.literal(EMPTY_FILTER_VALUE)])).optional(),
  probability_ids: z.array(z.union([z.number(), z.literal(EMPTY_FILTER_VALUE)])).optional(),
  is_overdue: z.string().optional(),
  page_number: z.number().optional(),
  page_size: z.number().optional(),
  term: z.string().optional(),
  related_records: z.array(RelatedRecordRef).optional(),
});

export type QualityEventsGet = z.infer<typeof QualityEventsGet>;

export const ZExportFormat = z.enum(['pdf', 'xlsx']);
export type ExportFormat = z.infer<typeof ZExportFormat>;

export const ZExportParams = QualityEventsGet.extend({
  format: ZExportFormat,
  columns: z.array(z.string()).optional(),
});
export type ExportParams = z.infer<typeof ZExportParams>;

const TitleLengthRestriction = z.object({
  title: z.string().max(100, 'Title can be no longer than 100 characters'),
});

export const QualityEventCreate = z.object({
  title: z.string(),
  workflow_id: z.string(),
  description: OptionalStringSchema,
  probability_id: OptionalStringSchema,
  severity_id: OptionalStringSchema,
  risk_id: OptionalStringSchema,
  owner_id: z.string(),
  related_entity_ids: OptionalStringArraySchema,
  suppliers: OptionalStringArraySchema,
  related_documents: OptionalStringArraySchema,
  related_events: OptionalStringArraySchema,
  tags_ids: OptionalStringArraySchema,
  due_date: z.string().optional().nullable(),
  escalated_to: OptionalNumberArraySchema,
  escalated_from: OptionalNumberArraySchema,
  collaborators: OptionalNumberArraySchema,
  related_products: OptionalStringArraySchema,
  products: z.array(ZProductRootCause).optional(),
  related_rootcauses: OptionalStringArraySchema,
  rootcauses: z.array(ZProductRootCause).optional(),
});
export const QualityEventCreateWithTitleRestriction = QualityEventCreate.merge(TitleLengthRestriction);

export type QualityEventCreate = z.infer<typeof QualityEventCreate>;
export type QualityEventCreateWithTitleRestriction = z.infer<typeof QualityEventCreateWithTitleRestriction>;

export const EventSortParam = z.object({
  id: z.enum([
    'created_at',
    '-created_at',
    'due_at',
    '-due_at',
    'owner',
    '-owner',
    'product',
    '-product',
    'risk',
    '-risk',
    'status',
    '-status',
    'title',
    '-title',
    'code',
    '-code',
    'issue_code',
    '-issue_code',
  ]),
  desc: z.boolean().optional(),
});

export type EventSortParam = z.infer<typeof EventSortParam>;

export const EventSortParams = z.array(EventSortParam);

export type EventSortParams = z.infer<typeof EventSortParams>;

export const eventSortParamsValidator = (params: unknown): params is EventSortParams => {
  return EventSortParams.safeParse(params).success;
};
